import { useState, useEffect, useRef } from 'react';
import { 
  Calendar, 
  Download, 
  FileText, 
  TrendingUp, 
  Heart, 
  Activity, 
  Bell, 
  ChevronLeft, 
  ChevronRight, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Printer,
  UserCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';
import useParentData from '@/hooks/useParentData';
import useHostelSettings from '@/hooks/useHostelSettings';
import ChildSelector from './ChildSelector';
import { format, startOfWeek, subWeeks, addDays, isSameDay, parseISO } from 'date-fns';

const WeeklyReports = () => {
  const { children, selectedChild, setSelectedChild, loading: loadingChild } = useParentData();
  const { settings, logoUrl } = useHostelSettings();
  const [reportData, setReportData] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [selectedWeekStart, setSelectedWeekStart] = useState('');

  // Generate list of available weeks (last 12 weeks)
  useEffect(() => {
    const weeks = [];
    const now = new Date();
    // Get Sunday of current week
    let currentSunday = startOfWeek(now, { weekStartsOn: 0 });
    
    for (let i = 0; i < 12; i++) {
      const start = subWeeks(currentSunday, i);
      const end = addDays(start, 6);
      weeks.push({
        start: format(start, 'yyyy-MM-dd'),
        end: format(end, 'yyyy-MM-dd'),
        label: `${format(start, 'MMM dd')} - ${format(end, 'MMM dd, yyyy')}`
      });
    }
    setAvailableWeeks(weeks);
    setSelectedWeekStart(weeks[0].start);
  }, []);

  useEffect(() => {
    if (selectedChild && selectedWeekStart) {
      fetchWeeklyReport(selectedChild.id, selectedWeekStart);
    }
  }, [selectedChild, selectedWeekStart]);

  const fetchWeeklyReport = async (studentId, weekStart) => {
    setLoadingReport(true);
    try {
      const response = await apiClient.get(`/v1/parent/reports/child/${studentId}/weekly`, {
        params: { week_start: weekStart }
      });
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to load weekly report');
      setReportData(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedChild || !selectedWeekStart) return;
    
    setLoadingReport(true);
    try {
      const response = await apiClient.get(`/v1/parent/reports/child/${selectedChild.id}/weekly/pdf`, {
        params: { week_start: selectedWeekStart },
        responseType: 'blob'
      });

      const fileName = `Weekly_Report_${selectedChild.name}_${selectedWeekStart}.pdf`;
      await downloadFile(response.data, fileName, 'application/pdf');
    } catch (error) {
      console.error('PDF error:', error);
      toast.error('Failed to generate PDF report');
    } finally {
      setLoadingReport(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      case 'absent': return <XCircle className="w-5 h-5 text-rose-500" />;
      case 'half-day': return <AlertCircle className="w-5 h-5 text-amber-500" />;
      default: return null;
    }
  };

  const getHealthBadge = (status) => {
    const config = {
      healthy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      sick: 'bg-rose-100 text-rose-700 border-rose-200',
      recovering: 'bg-amber-100 text-amber-700 border-amber-200'
    };
    return (
      <span className={`px-2 py-0.5 rounded text-[10px] font-bold border uppercase ${config[status?.toLowerCase()] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  if (loadingChild) return <div className="p-8 text-center text-slate-500 animate-pulse">Loading child data...</div>;

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-8 min-h-screen">
      
      {/* Non-Printable Controls */}
      <div className="print:hidden space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Weekly Performance Report</h1>
            <p className="text-slate-500">Track your child's weekly progress, attendance, and health</p>
          </div>
          <Button 
            onClick={handleDownloadPDF}
            className="bg-slate-800 hover:bg-slate-900 text-white shadow-lg transition-all"
          >
            <Printer className="w-4 h-4 mr-2" />
            Generate PDF
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChildSelector 
            children={children}
            selectedChild={selectedChild}
            onSelectChild={setSelectedChild}
          />
          
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col gap-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
              <Calendar className="w-3 h-3" /> Select Report Week
            </label>
            <select
              className="w-full p-2 bg-slate-50 border-none rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-slate-700 font-medium"
              value={selectedWeekStart}
              onChange={(e) => setSelectedWeekStart(e.target.value)}
            >
              {availableWeeks.map(week => (
                <option key={week.start} value={week.start}>{week.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loadingReport ? (
        <div className="flex flex-col items-center justify-center py-20 space-y-4">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Generating your child's weekly report...</p>
        </div>
      ) : reportData ? (
        <div className="report-container animate-fade-in">
          
          {/* Printable Report Header - Custom styled for PDF */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100">
            {/* Brand Header (Print Only) */}
            <div className="hidden print:flex items-center justify-between p-8 bg-slate-50 border-b-2 border-slate-200">
              <div className="flex items-center gap-4">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="w-16 h-16 object-contain rounded-xl" />
                ) : (
                  <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-2xl">
                    {settings.name?.charAt(0) || 'D'}
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tighter">{settings.name || 'DormCircle'}</h3>
                  <p className="text-xs font-bold text-blue-600 uppercase tracking-widest">Official Weekly Report</p>
                  {settings.address && <p className="text-[10px] text-slate-400 font-medium mt-0.5">{settings.address}</p>}
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase font-bold tracking-widest mb-1">Generated On</p>
                <p className="text-sm font-bold text-slate-700">{format(new Date(), 'PPP')}</p>
                <p className="text-[10px] text-slate-400 mt-1 italic">Report ID: DC-W-{reportData.student?.id?.slice(-6).toUpperCase()}</p>
              </div>
            </div>

            {/* Profile Section */}
            <div className="p-6 md:p-8 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div className="flex items-center gap-5">
                  <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center border border-white/20 shadow-inner">
                    <UserCircle className="w-12 h-12 text-blue-300" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-black tracking-tight uppercase italic">{reportData.student?.name}</h2>
                    <div className="flex items-center gap-3 mt-2 text-blue-200 font-medium text-sm">
                      <span className="px-2 py-0.5 bg-blue-500/20 rounded-md border border-blue-400/30">CLASS {reportData.student?.class_name}</span>
                      <span className="px-2 py-0.5 bg-white/10 rounded-md">ROOM {reportData.student?.room_number}</span>
                    </div>
                  </div>
                </div>
                <div className="text-left md:text-right">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Reporting Period</p>
                  <p className="text-xl font-black text-blue-400">
                    {format(parseISO(reportData.week_range.start), 'MMM dd')} - {format(parseISO(reportData.week_range.end), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>
            </div>

            {/* Metrics Dashboard */}
            <div className="p-6 md:p-8 bg-white border-b border-slate-100">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Attendance</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-slate-800">{reportData.summary.attendance.percentage}%</span>
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-full rounded-full" style={{ width: `${reportData.summary.attendance.percentage}%` }}></div>
                  </div>
                </div>

                <div className="space-y-1 relative group cursor-help">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] flex items-center gap-1">
                    Health Status
                    <span className="text-[9px] text-slate-400 bg-slate-100 border border-slate-200 rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">?</span>
                  </p>
                  
                  {/* Tooltip Card */}
                  <div className="absolute z-50 bottom-full left-0 mb-2 w-64 bg-slate-800 text-white text-[11px] rounded-lg p-3 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none leading-relaxed">
                    <p className="font-bold text-blue-400 mb-1">Health Score Counter</p>
                    <p className="text-slate-300">
                      Shows the number of days out of 7 the student was completely healthy. Days logged with <b>Sick</b> or <b>Hospital Visit</b> statuses will decrease this score.
                    </p>
                    <div className="absolute top-full left-4 -mt-1 border-4 border-transparent border-t-slate-800"></div>
                  </div>

                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-emerald-600">{reportData.summary.health.healthy_days}</span>
                    <span className="text-slate-400 font-bold text-sm">/ 7 DAYS</span>
                  </div>
                  <p className="text-xs font-bold text-slate-500">
                    {reportData.summary.health.healthy_days === 7 ? 'Perfect Health Record' :
                     reportData.summary.health.healthy_days >= 5 ? 'Generally Doing Well' :
                     'Requires Medical Attention'}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Activities Logged</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-blue-600">{reportData.summary.activities.count}</span>
                  </div>
                  <p className="text-xs font-bold text-slate-500">Engagement Score</p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Alerts/Notices</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-black text-amber-500">{reportData.summary.notices.count}</span>
                  </div>
                  <p className="text-xs font-bold text-slate-500">Weekly Notifications</p>
                </div>
              </div>
            </div>

            {/* Daily Breakdown Section */}
            <div className="p-6 md:p-8">
              <h3 className="text-lg font-black text-slate-800 uppercase tracking-widest mb-6 flex items-center gap-3">
                <Activity className="w-5 h-5 text-blue-600" />
                Detailed Daily Log
              </h3>
              
              <div className="space-y-6">
                {Object.values(reportData.days).map((day, idx) => (
                  <div key={idx} className="group relative pl-8 border-l-2 border-slate-100 pb-6 last:pb-0">
                    {/* Timeline Node */}
                    <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full border-2 border-white bg-blue-600 shadow-sm transition-all group-hover:scale-125"></div>
                    
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mb-3">
                      <div>
                        <h4 className="font-black text-slate-800 text-lg flex items-center gap-3 uppercase italic">
                          {day.day_name}
                          <span className="text-xs font-bold text-slate-400 normal-case not-italic tracking-normal">
                            {format(parseISO(day.date), 'MMM dd, yyyy')}
                          </span>
                        </h4>
                      </div>
                      <div className="flex items-center gap-4">
                         {day.attendance && (
                           <div className="flex items-center gap-2 px-3 py-1 bg-slate-50 rounded-lg border border-slate-100">
                             {getStatusIcon(day.attendance.status)}
                             <span className="text-xs font-black text-slate-600 uppercase italic">{day.attendance.status}</span>
                           </div>
                         )}
                         {day.health && day.health.length > 0 && getHealthBadge(day.health[0].status)}
                      </div>
                    </div>

                    {/* Day Content */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Activities */}
                      {day.activities && day.activities.length > 0 ? (
                        <div className="bg-slate-50/50 p-4 rounded-xl border border-dashed border-slate-200">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Activities</p>
                          <div className="space-y-3">
                            {day.activities.map((act, i) => {
                              const typeColors = {
                                sports: 'bg-emerald-50 border-emerald-100 text-emerald-600',
                                arts: 'bg-pink-50 border-pink-100 text-pink-600',
                                academic: 'bg-blue-50 border-blue-100 text-blue-600',
                                cultural: 'bg-purple-50 border-purple-100 text-purple-600',
                                other: 'bg-slate-50 border-slate-100 text-slate-500'
                              };
                              const badgeStyle = typeColors[act.activity_type?.toLowerCase()] || typeColors.other;
                              return (
                                <div key={i} className="flex flex-col gap-1 py-1.5 border-b last:border-0 border-slate-200/50">
                                  <div className="flex items-center gap-2">
                                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></div>
                                    <span className="text-sm font-bold text-slate-700">{act.title || act.activity_name || 'Activity'}</span>
                                    <span className={`text-[9px] px-1.5 py-0.2 border rounded-md font-bold uppercase tracking-wider ${badgeStyle}`}>
                                      {act.activity_type || 'other'}
                                    </span>
                                  </div>
                                  {act.description && (
                                    <p className="text-xs text-slate-500 pl-3.5 leading-relaxed font-medium">
                                      {act.description}
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 rounded-xl border border-dashed border-slate-200 flex items-center justify-center">
                          <p className="text-[10px] font-bold text-slate-300 uppercase italic">No Activities Recorded</p>
                        </div>
                      )}

                      {/* Remarks / Health Updates */}
                      <div className="space-y-4">
                        {day.health && day.health.length > 0 && (
                          <div className="bg-rose-50/30 p-4 rounded-xl border border-dashed border-rose-200">
                            <p className="text-[10px] font-black text-rose-600 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                              <Heart className="w-3.5 h-3.5" /> Health Updates
                            </p>
                            <div className="space-y-3">
                              {day.health.map((record, i) => {
                                const statusColors = {
                                  healthy: 'bg-emerald-50 border-emerald-100 text-emerald-600',
                                  sick: 'bg-rose-50 border-rose-100 text-rose-600',
                                  injured: 'bg-amber-50 border-amber-100 text-amber-600',
                                  unwell: 'bg-yellow-50 border-yellow-100 text-yellow-600',
                                  other: 'bg-slate-50 border-slate-100 text-slate-500'
                                };
                                const badgeStyle = statusColors[record.status?.toLowerCase()] || statusColors.other;
                                return (
                                  <div key={i} className="flex flex-col gap-1 py-1.5 border-b last:border-0 border-rose-200/30">
                                    <div className="flex items-center gap-2">
                                      <div className="w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0"></div>
                                      <span className="text-sm font-bold text-slate-700">Health Update</span>
                                      <span className={`text-[9px] px-1.5 py-0.2 border rounded-md font-bold uppercase tracking-wider ${badgeStyle}`}>
                                        {record.status || 'other'}
                                      </span>
                                    </div>
                                    {(record.symptoms || record.treatment) && (
                                      <div className="text-xs text-slate-500 pl-3.5 leading-relaxed font-medium space-y-0.5">
                                        {record.symptoms && (
                                          <p><span className="font-bold text-slate-600">Symptoms:</span> {record.symptoms}</p>
                                        )}
                                        {record.treatment && (
                                          <p><span className="font-bold text-slate-600">Treatment:</span> {record.treatment}</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {day.attendance?.remarks && (
                          <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                            <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-1">Remarks</p>
                            <p className="text-sm font-medium text-amber-800 italic">"{day.attendance.remarks}"</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notices Footer */}
            {reportData.notices && reportData.notices.length > 0 && (
              <div className="p-6 md:p-8 bg-slate-50 border-t border-slate-200">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-4 flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-500" />
                  Notices & Communications
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reportData.notices.map((notice, i) => (
                    <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-bold text-slate-800">{notice.title}</h4>
                        <span className="text-[10px] font-bold text-slate-400">{format(parseISO(notice.created_at), 'MMM dd')}</span>
                      </div>
                      <p className="text-xs text-slate-600 leading-relaxed">{notice.message}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Official Seal/Footer for Print */}
            <div className="hidden print:block p-8 border-t-2 border-slate-200 text-center">
              <div className="flex justify-between items-end">
                <div className="text-left">
                  <p className="text-xs font-bold text-slate-400 uppercase mb-8">Authorised Signature</p>
                  <div className="w-48 border-b-2 border-slate-800"></div>
                  <p className="text-sm font-black text-slate-800 mt-2 italic">Hostel Warden / Administrator</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold text-slate-300 leading-relaxed">
                    This is an electronically generated report.<br />
                    For any discrepancies, contact DormCircle support.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-8 text-center text-slate-400 text-xs font-medium uppercase tracking-[0.3em] pb-10">
            --- End of Report ---
          </div>
        </div>
      ) : !loadingReport && (
        <div className="bg-white rounded-2xl p-20 text-center border-2 border-dashed border-slate-200">
          <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-xl font-bold text-slate-800">No Report Data</h3>
          <p className="text-slate-500 mt-2 max-w-sm mx-auto">
            We couldn't find any records for this specific week. This usually happens if attendance hasn't been marked yet. Please try selecting a different week.
          </p>
        </div>
      )}

      {/* Global Print Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body { background: white !important; margin: 0; padding: 0; }
          .report-container { box-shadow: none !important; border: none !important; margin: 0 !important; width: 100% !important; }
          @page { size: auto; margin: 20mm; }
          nav, footer, .print-hidden { display: none !important; }
          .bg-slate-50 { background-color: #f8fafc !important; -webkit-print-color-adjust: exact; }
          .bg-slate-800 { background-color: #1e293b !important; -webkit-print-color-adjust: exact; }
          .text-white { color: white !important; }
          .text-blue-400 { color: #60a5fa !important; }
        }
      `}} />
    </div>
  );
};

export default WeeklyReports;
